import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Text,
  Layer,
  Table,
  TableHeader,
  TableRow,
  TableCell,
  TableBody,
  Notification,
} from "grommet";
import { View, SettingsOption, User } from "grommet-icons";
import axios from "axios";
import API_URL from "../../config/config";
import moment from "moment-timezone";

const AssessmentList = ({
  currentAssessments,
  sortedFilteredAssessments,
  itemsPerPage,
  currentPage,
  indexOfLastItem,
  showModal,
  selectedAssessment,
  showUsers,
  showTopics,
  setShowUsers,
  setShowTopics,
  closeModal,
  handlePreviousPage,
  handleNextPage,
  handleViewDetails,
  handleModifyAssessment,
  handleUserAccess,
  handleAssessmentRecommendation,
  requiredButton,
  navigate,
  archived,
}) => {
  // Set up notification state
  const [notification, setNotification] = useState(null);
  const handleInviteAgain = (assessment_id, userId) => {
    try {
      axios.get(
        `${API_URL}/manager/send_assessment_reminder/${assessment_id}/${userId}`,
      );
      setNotification({
        message: "Reminder sent successfully!",
        status: "normal",
      });
      // Clear the notification after 3 seconds
      setTimeout(() => {
        setNotification(null);
      }, 3000);
    } catch (error) {
      console.error("Error sending assessment reminder:", error);
      setNotification({
        message: "An error occurred while sending the assessment reminder.",
        status: "critical",
      });
      setTimeout(() => {
        setNotification(null);
      }, 3000);
    }
  };
  const [timezone, setTimezone] = useState("");

  useEffect(() => {
    const usertimezone = moment.tz.guess();
    setTimezone(usertimezone);
  }, []);

  return (
    <>
      {currentAssessments.length > 0 ? (
        currentAssessments.map((assessment) => (
          <Box
            key={assessment.assessment_id}
            margin={{ bottom: "medium" }}
            border={{ color: "brand", size: "small" }}
            round="small"
            background="light-1"
            pad="medium"
            elevation="small" // Adds slight shadow effect for a professional look
            overflow="hidden" // Prevents box content from overflowing
          >
            {/* Assessment Container */}
            <Box
              direction="row"
              justify="between"
              align="center"
              fill="horizontal"
              gap="small"
            >
              {/* Left: Assessment Details */}
              <Box basis="23.33%" pad={{ right: "small" }}>
                <Text weight="bold" size="medium">
                  {`Assessment Name: ${assessment.name || "Unnamed Assessment"}`}
                </Text>
                <Text size="small" margin={{ top: "xsmall" }} color="dark-3">
                  {`Due By: ${
                    assessment.due_by
                      ? new Date(assessment.due_by).toLocaleString([], {
                          year: "numeric",
                          month: "2-digit",
                          day: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : "No due date"
                  }`}
                </Text>
                <Text size="small" margin={{ top: "xsmall" }} color="dark-3">
                  {`${
                    assessment.users.filter(
                      (user) => user.isComplete === 1 || user.isComplete === 2,
                    ).length
                  }/${assessment.users.length} users have accepted this assessment.`}
                </Text>
              </Box>

              {/* Middle: Selected Skills Section */}
              <Box
                basis="33.33%"
                pad={{ vertical: "small", horizontal: "medium" }}
                background="light-2"
                round="xsmall"
                style={{ transform: "translateX(-10px)" }}
              >
                <Text size="small" weight="bold" margin={{ bottom: "xsmall" }}>
                  Selected skill(s)
                </Text>
                {assessment.assessmentTopics &&
                assessment.assessmentTopics.length > 0 ? (
                  <Box direction="column" gap="xsmall">
                    {assessment.assessmentTopics.map((topic, index) => (
                      <Text key={index} size="small">
                        {topic.product_name || "No Product Name"} -{" "}
                        {topic.skill_name || "No Skill Name"}
                      </Text>
                    ))}
                  </Box>
                ) : (
                  <Text size="small" color="status-unknown">
                    No Assessment Topics
                  </Text>
                )}
              </Box>

              {/* Right: Action Buttons */}
              <Box basis="43.33%" pad={{ left: "medium" }} align="end">
                {/* Button Wrapper */}
                <Box direction="row" align="end" gap="small">
                  {/* Ensures consistent height by keeping a placeholder when Assessment Details is not shown */}
                  {assessment.users.some(
                    (user) => user.isComplete === 1 || user.isComplete === 2,
                  ) ? (
                    <Button
                      icon={<View />}
                      label="Assessment Details"
                      onClick={() => handleViewDetails(assessment)}
                      secondary
                      plain={false}
                      style={{
                        borderRadius: "25px",
                        width: "200px",
                        height: "60px",
                      }}
                    />
                  ) : (
                    <Box height="48px" width="200px" /> // Placeholder to maintain consistency
                  )}

                  {/* Action Buttons */}
                  {requiredButton && (
                    <Box direction="row" gap="small" align="center">
                      <Button
                        icon={<SettingsOption />}
                        label="Edit Assessment"
                        onClick={() => handleModifyAssessment(assessment)}
                        secondary
                        plain={false}
                        style={{
                          borderRadius: "25px",
                          width: "200px",
                          height: "60px",
                        }}
                      />
                      <Button
                        icon={<User />}
                        label="User Access"
                        onClick={() => handleUserAccess(assessment)}
                        secondary
                        plain={false}
                        style={{
                          borderRadius: "25px",
                          width: "200px",
                          height: "60px",
                        }}
                      />
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        ))
      ) : (
        // Display message when no assessments match the search query
        <Box align="center" pad="medium">
          <Text>No assessments found.</Text>
        </Box>
      )}

      {/* Pagination Controls */}
      {sortedFilteredAssessments.length > itemsPerPage && (
        <Box
          direction="row"
          justify="center"
          align="center"
          margin={{ top: "medium" }}
          gap="small"
        >
          <Button
            label="Previous"
            onClick={handlePreviousPage}
            disabled={currentPage === 1}
            margin="small"
          />
          <Text>
            Page {currentPage} of{" "}
            {Math.ceil(sortedFilteredAssessments.length / itemsPerPage)}
          </Text>
          <Button
            label="Next"
            onClick={handleNextPage}
            disabled={indexOfLastItem >= sortedFilteredAssessments.length}
            margin="small"
          />
        </Box>
      )}

      {/* Modal for Assessment Details */}
      {showModal && selectedAssessment && (
        <Layer onEsc={closeModal} onClickOutside={closeModal}>
          <Box pad="medium" width="large">
            <Text weight="bold" size="large">
              Assessment Details
            </Text>

            {/* Additional Assessment Details can be added here */}

            {/* Buttons to Toggle Users and Topics */}
            <Box direction="row" gap="medium" margin={{ top: "medium" }} wrap>
              <Button
                label="Users"
                onClick={() => {
                  setShowUsers(!showUsers);
                  setShowTopics(false); // Hide topics when viewing users
                }}
                primary={showUsers}
                background="brand"
                hoverIndicator
              />
              <Button
                label="Skills"
                onClick={() => {
                  setShowTopics(!showTopics);
                  setShowUsers(false); // Hide users when viewing topics
                }}
                primary={showTopics}
                background="brand"
                hoverIndicator
              />
              {selectedAssessment.users.filter((user) => user.isComplete === 1)
                .length > 0 && (
                <Button
                  label="Course recommendations"
                  onClick={() => {
                    navigate(
                      `/manager/assessment/view-recommendations/${selectedAssessment.assessment_id}`,
                      {
                        state: {
                          name: selectedAssessment.name,
                          due_by: selectedAssessment.due_by,
                        },
                      },
                    );
                  }}
                  background="brand"
                  hoverIndicator
                />
              )}
            </Box>

            {/* Conditional rendering for Users */}

            {showUsers && (
              <Box margin={{ top: "medium" }}>
                <Text weight="bold" size="medium">
                  Users:
                </Text>
                {selectedAssessment.users?.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableCell>
                          <Text>User Name</Text>
                        </TableCell>
                        <TableCell>
                          <Text>Assessment accepted</Text>
                        </TableCell>
                        <TableCell>
                          <Text>Assessment Status</Text>
                        </TableCell>

                        <TableCell align="center">
                          <Text>Details</Text>
                        </TableCell>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedAssessment.users.map((user) => (
                        <TableRow key={user.user_id}>
                          <TableCell>
                            <Text>{user.name || "N/A"}</Text>
                          </TableCell>
                          <TableCell>
                            <Text>
                              {/* {user.invite_status === 0
                                ? "Pending"
                                : user.invite_status === 1
                                  ? "Accepted"
                                  : user.invite_status === 2
                                    ? "Declined"
                                    : user.invite_status === 3
                                      ? "Active"
                                      : "Deleted user"} */}
                              {user.isComplete === 0
                                ? "No"
                                : user.isComplete === 1
                                  ? "Yes"
                                  : user.isComplete === -1
                                    ? "No"
                                    : "Yes"}
                            </Text>
                          </TableCell>
                          <TableCell>
                            <Text>
                              {user.isComplete === 0
                                ? "Pending"
                                : user.isComplete === 1
                                  ? "Submitted"
                                  : user.isComplete === -1
                                    ? "Declined"
                                    : "Pending"}
                            </Text>
                          </TableCell>

                          {/* Render Details column conditionally */}

                          <TableCell>
                            {user.isComplete === 1 && user.assessmentInfo ? (
                              <Button
                                label="Recommendation"
                                onClick={() =>
                                  handleAssessmentRecommendation(
                                    selectedAssessment.assessment_id,
                                    user.user_id,
                                  )
                                }
                                secondary
                                style={{ padding: "4px 12px" }}
                                align="center"
                              />
                            ) : archived ? (
                              <Button
                                label="Invite Again"
                                onClick={() =>
                                  handleInviteAgain(
                                    selectedAssessment.assessment_id,
                                    user.user_id,
                                  )
                                }
                                secondary
                                style={{ padding: "4px 12px" }}
                                align="center"
                              />
                            ) : (
                              <Text align="center" alignSelf="center">
                                N/A
                              </Text>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <Text>No users added to this assessment.</Text>
                )}
              </Box>
            )}

            {/* Conditional rendering for Topics */}
            {showTopics && selectedAssessment.assessmentTopics.length > 0 && (
              <Box margin={{ top: "medium" }}>
                <Text weight="bold" size="medium">
                  Assessment Topics:
                </Text>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableCell>
                        <Text>Product Name</Text>
                      </TableCell>
                      <TableCell>
                        <Text>Skill Name</Text>
                      </TableCell>
                      <TableCell>
                        <Text>Question Count</Text>
                      </TableCell>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedAssessment.assessmentTopics.map((topic, index) => (
                      <TableRow key={index}>
                        <TableCell>{topic.product_name}</TableCell>
                        <TableCell>{topic.skill_name}</TableCell>
                        <TableCell>{topic.count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}

            {/* Close Button */}
            <Box align="center" pad="medium">
              <Button label="Close" onClick={closeModal} />
            </Box>
          </Box>
        </Layer>
      )}
      {/* Notification Toast */}
      {notification && (
        <Notification
          toast
          message={notification.message}
          status={notification.status}
          onClose={() => setNotification(null)}
        />
      )}
    </>
  );
};

export default AssessmentList;
